/* eslint-disable camelcase */
/* eslint-disable valid-jsdoc */
/* eslint-disable no-undefined */

import type {
  BookingResponse,
  BookingService,
  BookingState,
  Quest,
  Slot,
  TicketTariff
} from './types'
import type { PhoneInputController } from 'application/ui/fields/phone'
import {
  BookingStateManager,
  hasServicesStep,
  resolveFullPaymentAmount
} from './state'
import { BookingUIUpdater } from './ui-updaters'
import { BookingAPI } from './api'
import { BookingValidator } from './validation'
import { getYandexClientId, reachGoal } from 'application/utils/analytics'
import { renderWithCurrency } from 'application/utils/currency'
import {
  validateForm,
  setupCertificateCodeMask
} from 'application/utils/form-validation'
import { CounterField } from 'application/ui/fields/counter-field'
import Modals from 'application/ui/modal/modal'

interface RawQuestParams {
  zis_description?: string
  quest_id?: number | string
  quest_name?: string
  quest_photo?: string
  metro?: string
  address?: string
  players_text?: string
  time_text?: string
  age_text?: string
  available_languages?: Array<{ code: string; name: string }>
  default_phone_code?: string
  phone_format?: string
  currency?: string
  prices_url?: string
  promocode_url?: string
  id?: number | string
  name?: string
  location?: { metro?: string }
  gamersVerbose?: string
  players_min?: number | string
  players_max?: number | string
  lengthMinutes?: number | string
  ageRestrictionsSymbol?: string | number
  cover_thumbnail_url?: string
  additional_services?: BookingService[]
  ticket_system?: boolean
  ticket_system_tariffs?: TicketTariff[]
  ticket_visit_days_ahead?: number | string
  max_tickets?: number | string
}

/**
 * Разобрать data-quest-params (см. QuestPageHelper#timetable_quest_params) в Quest —
 * общая нормализация полей (разные исторические имена ключей), используется и при
 * клике по слоту расписания (onTimetableClick), и при прямом открытии попапа для
 * билетной системы (BookingModalController#openTicketSystem), у которой нет слота.
 * @param {string} raw - сырой JSON из data-quest-params
 * @return {Quest} нормализованные данные квеста для состояния бронирования
 */
export function parseQuestParams(raw: string): Quest {
  let questParams: RawQuestParams = {}

  try {
    questParams = JSON.parse(raw)
  } catch {
    questParams = {}
  }

  return {
    zis_description: questParams.zis_description,
    quest_id: questParams.quest_id ?? questParams.id ?? '',
    quest_name: questParams.quest_name || questParams.name || '',
    quest_photo:
      questParams.quest_photo || questParams.cover_thumbnail_url || '',
    metro: questParams.metro || questParams.location?.metro || '',
    players_text: questParams.players_text || questParams.gamersVerbose || '',
    players_min: Number(questParams.players_min) || undefined,
    players_max: Number(questParams.players_max) || undefined,
    time_text:
      questParams.time_text ||
      (questParams.lengthMinutes ? `${questParams.lengthMinutes} мин` : ''),
    age_text:
      questParams.age_text ||
      (questParams.ageRestrictionsSymbol
        ? `${questParams.ageRestrictionsSymbol}+`
        : ''),
    prices_url: questParams.prices_url || null,
    promocode_url: questParams.promocode_url,
    available_languages: questParams.available_languages || [],
    additional_services: questParams.additional_services || [],
    ticket_system: questParams.ticket_system || false,
    ticket_system_tariffs: questParams.ticket_system_tariffs || [],
    ticket_visit_days_ahead:
      Number(questParams.ticket_visit_days_ahead) || undefined,
    max_tickets: Number(questParams.max_tickets) || undefined
  }
}

/**
 * Класс для обработки событий модалки бронирования
 */
// Заказ, по которому ответ партнёра не получен, запоминается на время вкладки:
// экран ошибки закрывается перезагрузкой страницы, а повтор той же брони
// должен уйти с id прежнего заказа, иначе сервер создаст второй.
const PENDING_BOOKING_STORAGE_KEY = 'booking:pending-order'

export class BookingEventHandlers {
  private modal: HTMLElement
  private timetable: HTMLElement | null
  private stateManager: BookingStateManager
  private uiUpdater: BookingUIUpdater
  private api: BookingAPI
  private validator: BookingValidator
  private phoneController: PhoneInputController | null
  private playersCounter: CounterField | null = null
  private isUserAccepted: boolean = false
  // Защита от повторной отправки, пока предыдущий шаг ещё выполняет запрос к серверу
  private isSubmitting: boolean = false
  private bookingStatus: 'personal' | 'payment' | 'interrupt' | 'finished' =
    'personal'
  // unique_id заказа, сохранённого сервером на шаге запроса SMS-кода;
  // отправляется обратно в повторном запросе, чтобы сервер проверил код
  // против уже сохранённой записи (Order#validate_sms_code!).
  private pendingBookingId: string | null = null

  // Callbacks
  private onCloseBooking: () => void
  private onCreate: () => void
  private onOpen: (_quest: Quest, _slot: Slot) => void

  constructor(
    modal: HTMLElement,
    timetable: HTMLElement | null,
    stateManager: BookingStateManager,
    uiUpdater: BookingUIUpdater,
    api: BookingAPI,
    validator: BookingValidator,
    phoneController: PhoneInputController | null,
    onCloseBooking: () => void,
    onCreate: () => void,
    onOpen: (_quest: Quest, _slot: Slot) => void
  ) {
    this.modal = modal
    this.timetable = timetable
    this.stateManager = stateManager
    this.uiUpdater = uiUpdater
    this.api = api
    this.validator = validator
    this.phoneController = phoneController
    this.onCloseBooking = onCloseBooking
    this.onCreate = onCreate
    this.onOpen = onOpen
  }

  /**
   * Привязать все обработчики событий
   */
  bindAll(): void {
    this.bindStepSubmitEvents()
    this.bindStepNavigationEvents()
    this.bindCloseStepEvents()
    this.bindRealtimeStepValidation()
    this.initPlayersCounter()
    this.bindPlayersCounterEvents()
    // this.bindUnauthorizedForm()
    // this.bindPaymentMethods()
    this.bindAdditionalServices()
    this.bindCertificateCode()
    this.bindPromoCode()
    this.bindPrepayOptions()
    this.bindAgreeCheckbox()
    this.bindTicketVisitDate()
    this.bindTimetable()
    this.bindSuccessCloseButton()

    this.updateCurrentStepSubmitState(false)
  }

  // «Назад» — чисто клиентский переход: заказа на сервере ещё нет, выбранные
  // услуги остаются в state.
  private bindStepNavigationEvents(): void {
    this.modal.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      const previousStepButton = target.closest('[data-booking-prev-step]')

      if (!previousStepButton) return

      event.preventDefault()

      const currentStep = this.stateManager.getState().step
      if (currentStep !== 2 && currentStep !== 'confirm-phone') return

      if (currentStep === 'confirm-phone') {
        this.pendingBookingId = null
        this.clearSmsCodeInput()
        this.validator.hideCodeError()
        this.uiUpdater.hideSmsCode()
      }
      this.validator.clearError()
      this.stateManager.setState({ step: 1 })
      this.uiUpdater.updateAll()
      this.updateCurrentStepSubmitState(false)
    })
  }

  private bindCloseStepEvents(): void {
    const closeStep = this.modal.querySelector(
      '[data-booking-step="close"]'
    ) as HTMLElement | null
    const errorStep = this.modal.querySelector(
      '[data-booking-step="error"]'
    ) as HTMLElement | null

    closeStep?.addEventListener('click', (event) => {
      const target = event.target as HTMLElement

      const returnButton = target.closest('[data-close-return]')
      if (returnButton) {
        event.preventDefault()
        this.returnToBooking()
        return
      }

      const confirmButton = target.closest('[data-close-confirm]')
      if (confirmButton) {
        event.preventDefault()
        Modals.getInstance().closeModal(this.modal.dataset.modalName || '')
      }
    })

    errorStep?.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      const confirmButton = target.closest('[data-close-confirm]')

      if (!confirmButton) return

      event.preventDefault()
      Modals.getInstance().closeModal(this.modal.dataset.modalName || '')
      window.location.reload()
    })
  }

  /**
   * «Закрыть» на экране «Спасибо за бронирование»: бронь уже у партнёра,
   * поэтому страницу перезагружаем — клиент видит занятое время в расписании.
   */
  private bindSuccessCloseButton(): void {
    this.modal
      .querySelector<HTMLButtonElement>('[data-close-success]')
      ?.addEventListener('click', (event) => {
        event.preventDefault()
        Modals.getInstance().closeModal(this.modal.dataset.modalName || '')
        window.location.reload()
      })
  }

  private initPlayersCounter(): void {
    const counterElement = this.modal.querySelector(
      '[data-counter-field][data-name="booking-players-count"]'
    ) as HTMLElement | null

    if (!counterElement) return

    this.playersCounter = new CounterField(counterElement)
  }

  private bindPlayersCounterEvents(): void {
    this.modal.addEventListener(
      'counterfield:change',
      this.onPlayersCounterChange as EventListener
    )
    this.modal.addEventListener(
      'textfield:change',
      this.onPlayersCounterChange as EventListener
    )
  }

  private onPlayersCounterChange = (
    event: CustomEvent<{ name: string; value: number }>
  ): void => {
    const counterName = event.detail?.name
    if (
      counterName !== 'booking-players-count' &&
      counterName !== 'booking-tickets-count'
    ) {
      return
    }

    const minPlayers =
      counterName === 'booking-tickets-count'
        ? 1
        : this.stateManager.getMinPlayersCount()
    const maxPlayers =
      counterName === 'booking-tickets-count'
        ? Math.max(
            1,
            Number(this.stateManager.getState().slot?.tickets?.available) || 1
          )
        : this.stateManager.getMaxPlayersCount()
    const nextPlayersCount = Math.min(
      maxPlayers,
      Math.max(minPlayers, Number(event.detail.value) || minPlayers)
    )

    if (counterName === 'booking-tickets-count') {
      this.stateManager.setState({
        tickets: {
          count: nextPlayersCount
        }
      })
    } else {
      this.stateManager.setState({ playersCount: nextPlayersCount })
    }

    this.uiUpdater.updatePlayersCount(this.stateManager.getState())
    this.uiUpdater.updateQuestInfo(this.modal, this.stateManager.getState())
    this.uiUpdater.updatePrice()
  }

  /**
   * Обработчик клика по расписанию
   */
  private bindTimetable(): void {
    this.timetable?.addEventListener('click', this.onTimetableClick)
  }

  /**
   * @param event - событие клика
   */
  onTimetableClick = (event: Event): void => {
    const target = event.target as HTMLElement
    const slotElement = target.closest('[data-slot]') as HTMLElement

    this.bookingStatus = 'personal'
    this.pendingBookingId = null

    this.uiUpdater.hideSmsCode()
    this.clearSmsCodeInput()
    this.validator.hideCodeError()
    this.validator.clearError()

    if (slotElement) {
      const slotRaw = slotElement.getAttribute('data-slot') || '{}'
      let slotData: {
        date?: string
        time?: string
        prices?: Array<{
          players?: number | string
          players_count?: number | string
          price?: number
        }>
        prices_url?: string
        min_price?: number | string
        services?: Array<{
          service_id?: number
          name?: string
          min?: number
          max?: number
          price?: number
          description?: string
          is_hidden?: boolean | string
          is_required?: boolean
          payment_timing?: 'on_site' | 'with_booking'
        }>
        tickets?: {
          available?: number
          max_availability?: number
          price?: number
          description?: string
        } | null
        prices_text?: string
        integration_params?: {
          slot_id?: number | string
          timestamp?: string | number
        }
        prepay?: {
          partial_prepay?: string | number
          prepay_required?: boolean | string
          full_prepay_allowed?: boolean
        } | null
        lock_text?: string
        booked?: boolean | string
        is_booked?: boolean | string
        validate_phone?: boolean | string
        promocode_url?: string
      }
      try {
        slotData = JSON.parse(slotRaw)
      } catch {
        console.error('Error parsing slot data:', slotRaw)
        return
      }

      const questWrapper = slotElement.closest(
        '[data-quest-params]'
      ) as HTMLElement | null
      const questParamsRaw =
        questWrapper?.getAttribute('data-quest-params') ||
        this.timetable?.getAttribute('data-quest-params') ||
        '{}'

      let questParams: RawQuestParams

      try {
        questParams = JSON.parse(questParamsRaw)
      } catch {
        questParams = {}
      }

      const mergedSlotStateData = {
        ...questParams,
        ...slotData
      }

      const pricesByTicketsCount = (slotData.prices || []).reduce(
        (acc, entry) => {
          const gamersCount = entry.players
          const price = entry.price

          if (gamersCount === null || price === null) {
            return acc
          }

          if (
            Number.isNaN(Number(gamersCount)) ||
            Number.isNaN(Number(price))
          ) {
            return acc
          }

          acc.push({
            gamers_count: Number(gamersCount),
            price: Number(price),
            percentage: 0
          })

          return acc
        },
        [] as Array<{ gamers_count: number; price: number; percentage: number }>
      )

      const minPriceCandidates = [
        Number(slotData.min_price),
        Number(slotData.tickets?.price),
        ...pricesByTicketsCount.map((entry) => entry.price)
      ].filter((value) => Number.isFinite(value) && value >= 0)
      const minPrice =
        minPriceCandidates.length > 0 ? Math.min(...minPriceCandidates) : 0

      const quest: Quest = parseQuestParams(questParamsRaw)
      quest.prices_url = slotData.prices_url || quest.prices_url

      const rawBooked = slotData.is_booked ?? slotData.booked ?? false
      const isBooked = rawBooked === true || rawBooked === 'true'

      const slot: Slot = {
        ...mergedSlotStateData,
        date: String(slotData.date || ''),
        time: String(slotData.time || ''),
        prices: (slotData.prices || [])
          .filter((entry) => entry.players !== null && entry.price !== null)
          .map((entry) => ({
            players: Number(entry.players),
            players_count: entry.players_count
              ? String(entry.players_count)
              : undefined,
            price: Number(entry.price)
          })),
        prices_text: slotData.prices_text,
        tickets: slotData.tickets,
        services: slotData.services?.map((service) => ({
          service_id: Number(service.service_id || 0),
          name: service.name || '',
          min: Number(service.min || 0),
          max: Number(service.max || 0),
          price: Number(service.price || 0),
          description: service.description || '',
          is_hidden: service.is_hidden,
          is_required: service.is_required,
          payment_timing: service.payment_timing
        })),
        booked: isBooked,
        min_price: minPrice,
        available_payment_backends: ['kassa', 'paypal'],
        lock_text: slotData.lock_text,
        promocode_url: slotData.promocode_url || questParams.promocode_url,
        prices_by_tickets_count: pricesByTicketsCount,
        integration_params: slotData.integration_params,
        prepay: slotData.prepay,
        validate_phone:
          slotData.validate_phone === true || slotData.validate_phone === 'true'
      }

      // Вызвать callback открытия
      this.onOpen(quest, slot)
      this.updateCurrentStepSubmitState(false)
    }
  }

  /**
   * События закрытия
   */
  public closeModalHandler(): void {
    const state = this.stateManager.getState()
    const isTerminalStep = state.step === 'success' || state.step === 'error'
    // Заказ создаётся и уходит партнёру только по «Забронировать» (gameId),
    // после этого закрытие окна ничего не отменяет — но расписание надо
    // перечитать, чтобы занятое время исчезло из свободных.
    const bookingExists = Boolean(state.gameId)

    if (this.bookingStatus === 'finished' || isTerminalStep || bookingExists) {
      Modals.getInstance().closeModal(this.modal.dataset.modalName || '')
      if (bookingExists || isTerminalStep) {
        window.location.reload()
      }
      return
    }

    if (this.bookingStatus === 'interrupt') {
      Modals.getInstance().closeModal(this.modal.dataset.modalName || '')
      return
    }

    // Закрытие модалки
    this.showCloseConfirmation()
  }

  /**
   * События отправки формы
   */
  private bindStepSubmitEvents(): void {
    const bookingForm = this.getBookingForm()

    bookingForm?.addEventListener('submit', (event) => {
      event.preventDefault()

      if (this.isSubmitting) return

      this.updateCurrentStepSubmitState(true)
      const submitButton = this.getCurrentStepSubmitButton()
      if (submitButton?.disabled) return

      const currentStep = this.stateManager.getState().step
      const stopSubmitting = () => {
        this.isSubmitting = false
      }

      this.isSubmitting = true

      if (currentStep === 1) {
        this.submitStepOne().finally(stopSubmitting)
      } else if (currentStep === 'confirm-phone') {
        this.submitConfirmPhone().finally(stopSubmitting)
      } else if (currentStep === 2) {
        this.submitStepTwo().finally(stopSubmitting)
      } else if (currentStep === 3) {
        this.submitStepThree().finally(stopSubmitting)
      } else {
        stopSubmitting()
      }
    })
  }

  private bindRealtimeStepValidation(): void {
    const onFieldChange = (event: Event): void => {
      const target = event.target
      if (
        target instanceof HTMLInputElement &&
        target.matches('[data-code-input]')
      ) {
        this.validator.hideCodeError()
      }

      const runValidation =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
          ? target.required
          : false

      this.updateCurrentStepSubmitState(runValidation)
    }

    this.modal.addEventListener('input', onFieldChange)
    this.modal.addEventListener('change', onFieldChange)
    // Ошибка под полем появляется только после того, как из него вышли:
    // пока клиент печатает, подсказки не мешают, а пустые соседние поля не
    // подсвечиваются разом.
    this.modal.addEventListener('focusout', (event) => {
      if (this.markFieldTouched(event.target)) {
        this.updateCurrentStepSubmitState(true)
      }
    })
  }

  /**
   * Помечает обёртку поля как тронутую пользователем. validateForm проверяет
   * всю форму сразу, поэтому без такой пометки ввод имени сразу подсвечивал
   * бы ошибками пустые email и телефон.
   * @param {EventTarget | null} target - элемент, с которым взаимодействовали
   * @return {boolean} true, если это поле формы бронирования
   */
  private markFieldTouched(target: EventTarget | null): boolean {
    if (
      !(
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      )
    ) {
      return false
    }

    const field = target.closest<HTMLElement>('[data-field]')
    if (!field) return false

    field.dataset.touched = 'true'
    return true
  }

  /**
   * Сбросить пометки «тронуто» (при закрытии модалки форма очищается).
   */
  resetTouchedFields(): void {
    this.modal
      .querySelectorAll<HTMLElement>('[data-field][data-touched]')
      .forEach((field) => {
        delete field.dataset.touched
      })
  }

  private getCurrentStepSubmitButton(): HTMLButtonElement | null {
    const currentStep = this.stateManager.getState().step
    const currentStepContainer = this.modal.querySelector(
      `[data-booking-step="${currentStep}"]`
    ) as HTMLElement | null

    return (
      (currentStepContainer?.querySelector(
        'button[type="submit"]'
      ) as HTMLButtonElement | null) || null
    )
  }

  private updateCurrentStepSubmitState(runValidation: boolean): void {
    const submitButton = this.getCurrentStepSubmitButton()
    if (!submitButton) return

    const currentStep = this.stateManager.getState().step

    const acceptanceInput =
      currentStep === 1
        ? (this.modal.querySelector(
            '[data-booking-step="1"]:not(.is-hidden) input[name="acceptance"]:not(:disabled)'
          ) as HTMLInputElement | null)
        : null

    const acceptanceChecked =
      currentStep === 1 && acceptanceInput ? acceptanceInput.checked : true

    const form = this.getBookingForm()

    let formValid = true

    if (form) {
      formValid = runValidation
        ? this.validateFormWithoutHiddenFields(form)
        : this.checkRequiredFieldsFilled(form)
    }

    let smsCodeValid = true
    if (
      currentStep === 'confirm-phone' ||
      (currentStep === 1 && this.isSmsCodeVisible())
    ) {
      smsCodeValid = this.isSmsCodeValid()
    }

    submitButton.disabled = !(acceptanceChecked && formValid && smsCodeValid)
  }

  private getSmsCodeInput(): HTMLInputElement | null {
    return this.modal.querySelector(
      '[data-code-input]'
    ) as HTMLInputElement | null
  }

  private isSmsCodeVisible(): boolean {
    const smsCodeBlock = this.modal.querySelector(
      '[data-sms-code]'
    ) as HTMLElement | null

    if (!smsCodeBlock || smsCodeBlock.classList.contains('is-hidden')) {
      return false
    }

    const stepContainer = smsCodeBlock.closest(
      '[data-booking-step]'
    ) as HTMLElement | null

    return Boolean(
      stepContainer && !stepContainer.classList.contains('is-hidden')
    )
  }

  private sanitizeSmsCode(rawValue: string): string {
    return rawValue.replace(/\D/g, '').slice(0, 4)
  }

  private isSmsCodeValid(): boolean {
    const codeInput = this.getSmsCodeInput()
    if (!codeInput) return false

    const normalized = this.sanitizeSmsCode(codeInput.value)
    if (codeInput.value !== normalized) {
      codeInput.value = normalized
    }

    return /^\d{4}$/.test(normalized)
  }

  private clearSmsCodeInput(): void {
    const codeInput = this.getSmsCodeInput()
    if (!codeInput) return

    codeInput.value = ''
  }

  private getBookingForm(): HTMLFormElement | null {
    return this.modal.querySelector(
      '[data-booking-form]'
    ) as HTMLFormElement | null
  }

  private getStepOneForm(): HTMLFormElement | null {
    return this.getBookingForm()
  }

  private getStepOneInputValue(name: string): string {
    const form = this.getStepOneForm()
    if (!form) return ''

    const input = form.querySelector(
      `input[name="${name}"]`
    ) as HTMLInputElement | null

    return input?.value.trim() || ''
  }

  private getStepOneTextareaValue(name: string): string {
    const form = this.getStepOneForm()
    if (!form) return ''

    const textarea = form.querySelector(
      `textarea[name="${name}"]`
    ) as HTMLTextAreaElement | null

    return textarea?.value.trim() || ''
  }

  private getPhoneValue(): string {
    const phoneInput = this.modal.querySelector(
      '[data-phone-input]'
    ) as HTMLInputElement | null

    if (!phoneInput) {
      return this.phoneController?.getPhone() || ''
    }

    const controller = (
      phoneInput as HTMLInputElement & {
        phoneInputController?: { getPhone: () => string }
      }
    ).phoneInputController

    return controller?.getPhone()?.trim() || phoneInput.value.trim()
  }

  private validateFormWithoutHiddenFields(form: HTMLFormElement): boolean {
    const hiddenControls = Array.from(
      form.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >('.is-hidden input, .is-hidden textarea, .is-hidden select')
    )

    const enabledBeforeValidation: Array<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    > = []

    hiddenControls.forEach((control) => {
      if (!control.disabled) {
        control.disabled = true
        enabledBeforeValidation.push(control)
      }
    })

    const isValid = validateForm(form)

    enabledBeforeValidation.forEach((control) => {
      control.disabled = false
    })

    // Валидность считается по всей форме (кнопка остаётся выключенной), но
    // текст ошибки остаётся только у полей, которые пользователь уже трогал.
    form
      .querySelectorAll<HTMLElement>('[data-field]:not([data-touched])')
      .forEach((field) => {
        const errorElement = field.querySelector<HTMLElement>(
          ':scope > [data-error]'
        )
        if (errorElement) errorElement.textContent = ''
      })

    return isValid
  }

  private checkRequiredFieldsFilled(form: HTMLFormElement): boolean {
    const requiredControls = Array.from(
      form.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >('input[required], textarea[required], select[required]')
    ).filter((control) => !control.disabled && !control.closest('.is-hidden'))

    return requiredControls.every((control) => {
      if (control instanceof HTMLInputElement && control.type === 'checkbox') {
        return control.checked
      }

      return control.value.trim() !== ''
    })
  }

  /**
   * Показать подтверждение закрытия
   */
  private showCloseConfirmation(): void {
    const state = this.stateManager.getState()
    const visibleStep = this.modal.querySelector(
      '[data-booking-step="1"]:not(.is-hidden), [data-booking-step="2"]:not(.is-hidden), [data-booking-step="3"]:not(.is-hidden)'
    ) as HTMLElement | null
    const visibleStepValue = Number(
      visibleStep?.getAttribute('data-booking-step')
    )
    const currentStepNumber = typeof state.step === 'number' ? state.step : 0
    const closeStep = visibleStepValue > 1 || currentStepNumber > 1 ? 2 : 1

    this.bookingStatus = 'interrupt'
    this.stateManager.setState({
      isClosing: true,
      closeStep
    })

    this.modal
      .querySelector<HTMLElement>('[data-modal-content]')
      ?.classList.add('is-closing')

    this.uiUpdater.updateAll()
  }

  /**
   * Вернуться к бронированию
   */
  private returnToBooking(): void {
    this.bookingStatus = 'personal'
    this.stateManager.setState({ isClosing: false })
    this.uiUpdater.updateAll()

    this.modal
      .querySelector<HTMLElement>('[data-modal-content]')
      ?.classList.remove('is-closing')
  }

  /**
   * «Забронировать» — единственный запрос к серверу за всё бронирование.
   * Сервер создаёт заказ, отправляет его партнёру и, если указан, гасит
   * сертификат. До этого клика всё, что ввёл клиент (контакты, услуги,
   * промокод, сертификат), живёт только в state и в полях формы.
   */
  private async submitBooking(): Promise<void> {
    const state = this.stateManager.getState()
    const codeInput = this.getSmsCodeInput()
    const smsCode = codeInput ? this.sanitizeSmsCode(codeInput.value) : ''
    const name = this.getStepOneInputValue('name')
    const email = this.getStepOneInputValue('email')
    const userComment = this.getStepOneTextareaValue('user_comment')
    const phone = this.getPhoneValue()
    const services = this.getServicesPayload()
    const servicesTotal = this.stateManager.calculateServicesTotal()
    const total = this.stateManager.calculateTotal()
    const isTicketsMode = Boolean(state.slot?.tickets)
    const isTariffMode = Boolean(state.quest?.prices_url)

    this.stateManager.setState({ name, email, phone })
    this.validator.clearError()
    // Повторная отправка с кодом из SMS — та же попытка бронирования.
    if (!smsCode) reachGoal('new_BookingSlot')

    try {
      // Количество игроков определяет выбранный тариф; отдельного счётчика
      // в форме нет — решение: все квесты бронируются единообразно, состав
      // задаётся тарифной сеткой из MyERP.
      const countPayload =
        !isTicketsMode && state.selectedTariff?.players
          ? { count: state.selectedTariff.players }
          : {}

      const ticketsPayload = isTicketsMode
        ? {
            tickets: state.tickets?.count || 1
          }
        : {}

      const tariffPayload =
        isTariffMode && !isTicketsMode && state.selectedTariff
          ? {
              tariff: {
                name: state.selectedTariff.label,
                price: state.selectedTariff.price,
                players: state.selectedTariff.players
              }
            }
          : {}

      // Билетная система (Quest#ticket_system): нет слота/даты — количество
      // билетов на каждый выбранный тариф (см. BookingUIUpdater#renderTicketTariff).
      const ticketTariffsPayload = state.quest?.ticket_system
        ? {
            ticket_tariffs: Object.entries(state.ticketTariffs).map(
              ([index, quantity]) => ({
                tariff_index: Number(index),
                quantity
              })
            )
          }
        : {}

      if (!this.pendingBookingId) {
        this.pendingBookingId = this.restorePendingBooking(state)
      }

      const bookingPayload = {
        quest_id: String(state.quest?.quest_id || ''),
        date: String(state.slot?.date || ''),
        ...countPayload,
        total,
        name,
        zis_creator: this.getStepOneInputValue('zis_creator'),
        phone,
        email,
        slot_time: String(state.slot?.time || ''),
        integration_params: state.slot?.integration_params,
        services,
        services_total: servicesTotal,
        prepay: state.slot?.prepay ?? null,
        yandex_client_id: getYandexClientId(),
        ...ticketsPayload,
        ...tariffPayload,
        ...ticketTariffsPayload,
        ...(this.pendingBookingId ? { id: this.pendingBookingId } : {}),
        ...(smsCode ? { sms_code: smsCode } : {}),
        ...(userComment ? { user_comment: userComment } : {}),
        ...(state.promoCode ? { promo_code: state.promoCode } : {}),
        ...(state.certificateNumber
          ? { certificate_number: state.certificateNumber }
          : {}),
        ...(state.certificateCode
          ? { certificate_code: state.certificateCode }
          : {})
      }

      const bookingData = await this.api.createBooking(bookingPayload)

      // Квест с проверкой телефона: сервер сохранил заказ и отправил SMS,
      // бронь уйдёт партнёру после повторной отправки с кодом.
      if (bookingData.validate_phone) {
        this.showPhoneConfirmation(bookingData)
        return
      }

      if (bookingData.success === false || bookingData.result === false) {
        // Сервер вернул id, если ответ партнёра не получен: следующая попытка
        // повторит этот же заказ, а не создаст второй.
        this.pendingBookingId = bookingData.data?.id
          ? String(bookingData.data.id)
          : null
        this.persistPendingBooking(state)

        if (bookingData.error_code) {
          this.showBookingFailure(bookingData.error_code)
          return
        }

        this.showSubmitError(
          bookingData.error_text ||
            bookingData.user_msg_display ||
            bookingData.error ||
            'Ошибка создания бронирования'
        )
        return
      }

      const bookingId = bookingData.data?.id || bookingData.id
      if (!bookingId) {
        this.showSubmitError('Ошибка создания бронирования')
        return
      }

      this.applyBookingResponse(String(bookingId), bookingData)
    } catch (error) {
      this.showSubmitError(
        error instanceof Error ? error.message : 'Ошибка создания бронирования'
      )
    }
  }

  /**
   * Ошибка в данных формы (сертификат, тариф, количество): сообщение внутри
   * той же формы, введённые данные остаются, клиент исправляет их сам.
   */
  private showSubmitError(message: string): void {
    this.validator.showError(message)
    this.modal
      .querySelector<HTMLElement>('[data-booking-error]')
      ?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    this.updateCurrentStepSubmitState(true)
  }

  /**
   * Партнёр отказал или слот заняли: экран «Не удалось оформить бронирование»
   * по макету. Для занятого времени свой текст, иначе общий с телефоном.
   * «Закрыть» перезагружает страницу, чтобы расписание обновилось.
   */
  private showBookingFailure(code: string): void {
    const occupied = code === 'timeslot_occupied'

    this.modal
      .querySelectorAll<HTMLElement>('[data-booking-error-variant]')
      .forEach((element) => {
        const isOccupiedText =
          element.dataset.bookingErrorVariant === 'occupied'
        element.hidden = isOccupiedText !== occupied
      })

    this.validator.clearError()
    this.stateManager.setState({ step: 'error' })
    this.uiUpdater.updateAll()
  }

  private persistPendingBooking(state: BookingState): void {
    if (!this.pendingBookingId) {
      this.forgetPendingBooking()
      return
    }

    try {
      globalThis.sessionStorage.setItem(
        PENDING_BOOKING_STORAGE_KEY,
        JSON.stringify({
          id: this.pendingBookingId,
          questId: String(state.quest?.quest_id || ''),
          date: String(state.slot?.date || ''),
          time: String(state.slot?.time || '')
        })
      )
    } catch {
      // Хранилище недоступно (приватный режим): повтор уйдёт без id.
    }
  }

  private restorePendingBooking(state: BookingState): string | null {
    try {
      const raw = globalThis.sessionStorage.getItem(PENDING_BOOKING_STORAGE_KEY)
      if (!raw) return null

      const saved = JSON.parse(raw) as {
        id?: string
        questId?: string
        date?: string
        time?: string
      }
      const sameSlot =
        saved.questId === String(state.quest?.quest_id || '') &&
        saved.date === String(state.slot?.date || '') &&
        saved.time === String(state.slot?.time || '')

      return sameSlot && saved.id ? String(saved.id) : null
    } catch {
      return null
    }
  }

  private forgetPendingBooking(): void {
    try {
      globalThis.sessionStorage.removeItem(PENDING_BOOKING_STORAGE_KEY)
    } catch {
      // Хранилище недоступно — забывать нечего.
    }
  }

  private showPhoneConfirmation(bookingData: BookingResponse): void {
    this.pendingBookingId =
      String(bookingData.data?.id || '') || this.pendingBookingId

    const phoneLabel = this.modal.querySelector<HTMLElement>(
      '[data-confirm-phone-number]'
    )
    if (phoneLabel) {
      phoneLabel.textContent =
        this.stateManager.getState().phone || this.getPhoneValue()
    }

    this.stateManager.setState({ step: 'confirm-phone' })
    this.clearSmsCodeInput()
    this.uiUpdater.updateAll()
    this.uiUpdater.showSmsCode()
    if (bookingData.error_text || bookingData.error) {
      this.validator.showCodeError(
        bookingData.error_text || bookingData.error || ''
      )
    } else {
      this.validator.hideCodeError()
    }
    this.updateCurrentStepSubmitState(false)
  }

  private finiteNumber(value: unknown): number | null {
    const parsed = Number(value)

    return Number.isFinite(parsed) ? parsed : null
  }

  /**
   * Бронь подтверждена партнёром. Дальше либо экран оплаты (обязательная
   * предоплата или доступный онлайн-платёж), либо сразу «Спасибо».
   * Списанный сертификат делает бронь оплаченной — онлайн-платёж после него
   * сервер не создаёт, остаток доплачивается на месте.
   */
  private applyBookingResponse(
    bookingId: string,
    bookingData: BookingResponse
  ): void {
    const data = bookingData.data
    const prepayMode = this.normalizePrepayMode(data?.prepay)
    const paymentMethodsRaw = data?.payment_methods ?? null
    let paymentMethodsFromApi: BookingState['paymentMethods'] = null

    if (paymentMethodsRaw !== null) {
      paymentMethodsFromApi = Array.isArray(paymentMethodsRaw)
        ? paymentMethodsRaw.map((method) => ({
            name: method.name,
            title: method.title,
            icon: method.icon
          }))
        : Object.entries(paymentMethodsRaw).map(([methodName, methodData]) => ({
            name: methodName,
            title: methodData.title || methodName,
            icon: methodData.icon || ''
          }))
    }

    const certificate = data?.certificate
    // Онлайн-варианты есть только когда сервер разрешил онлайн-оплату
    // (после сертификата или без кассы остаётся одна «Оплата на месте»).
    const onlinePaymentAvailable = Boolean(
      data?.payment_required || data?.online_payment_available
    )
    // Скидка промокода уже вычтена сервером из booking_price — запоминаем её
    // сумму, чтобы не вычитать повторно; если сервер скидку не применил,
    // в итоге её не показываем.
    const serverDiscount = Number(data?.discount_amount || 0)
    const state = this.stateManager.getState()
    const promoDiscount =
      state.promoDiscount && serverDiscount > 0
        ? { ...state.promoDiscount, amount: serverDiscount }
        : null

    this.stateManager.setState({
      gameId: bookingId,
      promoDiscount,
      bookingPrice: this.finiteNumber(data?.booking_price),
      servicesPrice: this.finiteNumber(data?.services_price),
      services: this.servicesStateFromPayload(data?.services),
      prepayMode,
      fullOnlinePaymentDisabled: Boolean(data?.full_online_payment_disabled),
      prepayAmount: this.finiteNumber(data?.prepay_amount),
      fullOnlinePaymentPrice: this.finiteNumber(
        data?.full_online_payment_price
      ),
      // Сертификат зачтён сервером ровно на эту сумму — она уже вычтена из
      // остатка «на месте»; несписанный сертификат из итога убираем.
      certificateDiscount: certificate ? Number(certificate.amount) : null,
      certificateSummary: this.certificateSummaryText(
        certificate,
        data?.certificate_error
      ),
      // Бронь подтверждена — всегда экран «Квест забронирован» с разбивкой:
      // онлайн-варианты, если они есть, иначе единственная «Оплата на месте».
      step: 3,
      time: Date.now(),
      paymentType: paymentMethodsFromApi?.[0]?.name || 'cash',
      paymentTiming:
        onlinePaymentAvailable &&
        (prepayMode === 'required' || prepayMode === 'partial_required')
          ? 'prepay'
          : 'later',
      partial:
        onlinePaymentAvailable &&
        (prepayMode === 'required' ||
          prepayMode === 'partial_required' ||
          prepayMode === 'optional_partial'),
      paymentMethods: onlinePaymentAvailable ? paymentMethodsFromApi : []
    })

    this.bookingStatus = 'payment'
    this.pendingBookingId = null
    this.forgetPendingBooking()
    this.uiUpdater.hideSmsCode()
    this.uiUpdater.updateAll()
    this.updateCurrentStepSubmitState(false)

    // Вызвать callback onCreate
    this.onCreate()
  }

  private certificateSummaryText(
    certificate:
      | { amount?: number; remaining_amount?: number; currency?: string }
      | undefined,
    certificateError: string | undefined
  ): string | null {
    if (certificate) {
      const amount = Number(certificate.amount || 0)
      const remaining = Number(certificate.remaining_amount || 0)
      const currency =
        certificate.currency || this.stateManager.getState().certificateCurrency
      const unit = currency === 'BYN' ? 'BYN' : '₽'
      return `Сертификатом оплачено ${amount} ${unit}. Осталось оплатить на месте: ${remaining} ${unit}.`
    }

    if (certificateError) {
      return `Сертификат не списан: ${certificateError} Бронь подтверждена, оплатить её можно на месте.`
    }

    return null
  }

  private bindPrepayOptions(): void {
    const prepayOnlyCheckbox = this.modal.querySelector(
      'input[name="prepay-only"]'
    ) as HTMLInputElement | null
    const prepayFullCheckbox = this.modal.querySelector(
      'input[name="prepay-full"]'
    ) as HTMLInputElement | null

    const onPrepayChange = (event: Event): void => {
      const target = event.target as HTMLInputElement
      const state = this.stateManager.getState()

      if (
        target.name === 'prepay-only' &&
        target.checked &&
        prepayFullCheckbox
      ) {
        prepayFullCheckbox.checked = false
      }

      if (
        target.name === 'prepay-full' &&
        target.checked &&
        prepayOnlyCheckbox
      ) {
        prepayOnlyCheckbox.checked = false
      }

      let partial = false

      if (
        state.prepayMode === 'required' ||
        state.prepayMode === 'partial_required'
      ) {
        partial = !prepayFullCheckbox?.checked
      } else if (
        state.prepayMode === 'optional' ||
        state.prepayMode === 'optional_partial'
      ) {
        partial = Boolean(prepayOnlyCheckbox?.checked)
      }

      this.stateManager.setState({ partial })
      this.uiUpdater.updatePrice()
    }

    prepayOnlyCheckbox?.addEventListener('change', onPrepayChange)
    prepayFullCheckbox?.addEventListener('change', onPrepayChange)
  }

  /**
   * Шаг 1: контакты и состав заказа. Никаких запросов к серверу: если у квеста
   * есть доп.услуги — показываем шаг 2, иначе сразу бронируем.
   */
  private async submitStepOne(): Promise<void> {
    this.stateManager.setState({
      name: this.getStepOneInputValue('name'),
      email: this.getStepOneInputValue('email'),
      phone: this.getPhoneValue()
    })

    if (!hasServicesStep(this.stateManager.getState())) {
      await this.submitBooking()
      return
    }

    this.validator.clearError()
    this.stateManager.setState({ step: 2 })
    this.uiUpdater.updateAll()
    this.updateCurrentStepSubmitState(false)
  }

  /**
   * Подтверждение телефона: та же отправка бронирования, но с кодом из SMS и
   * id заказа, который сервер сохранил при первой попытке.
   */
  private async submitConfirmPhone(): Promise<void> {
    if (!this.isSmsCodeValid()) {
      this.validator.showCodeError('Введите 4 цифры из SMS')
      this.updateCurrentStepSubmitState(true)
      return
    }

    this.validator.hideCodeError()
    await this.submitBooking()
  }

  /**
   * Шаг 2: услуги, промокод и сертификат уже в state — бронируем.
   */
  private async submitStepTwo(): Promise<void> {
    await this.submitBooking()
  }

  private normalizePrepayMode(prepay: unknown): BookingState['prepayMode'] {
    if (
      prepay === 'none' ||
      prepay === 'optional' ||
      prepay === 'required' ||
      prepay === 'optional_partial' ||
      prepay === 'optional_full' ||
      prepay === 'partial_required' ||
      prepay === 'full_required'
    ) {
      return prepay
    }

    return 'none'
  }

  /**
   * Оплата на месте (или нечего платить онлайн): бронь уже подтверждена
   * партнёром при «Забронировать», письмо отправлено — просто закрываем окно
   * и перечитываем расписание, чтобы занятое время исчезло.
   */
  private finishBooking(): void {
    this.bookingStatus = 'finished'
    Modals.getInstance().closeModal(this.modal.dataset.modalName || '')
    window.location.reload()
  }

  /**
   * Шаг 3: оплата
   */
  private async submitStepThree(): Promise<void> {
    let state = this.stateManager.getState()
    if (!state.gameId) return

    if (this.getOrderTotal(state) === 0) {
      this.finishBooking()
      return
    }

    const hasRequiredSlotPrepay = this.isSlotPrepayRequired(state)
    const hasOnlinePaymentMethods = Boolean(state.paymentMethods?.length)

    if (
      hasOnlinePaymentMethods &&
      hasRequiredSlotPrepay &&
      state.paymentTiming === 'later'
    ) {
      this.stateManager.setState({ paymentTiming: 'prepay', partial: true })
      state = this.stateManager.getState()
    }

    if (
      (!hasOnlinePaymentMethods || !hasRequiredSlotPrepay) &&
      state.paymentTiming === 'later'
    ) {
      this.finishBooking()
      return
    }

    try {
      const paymentData = this.buildPaymentDataForPay(state)
      const paymentUrl = await this.api.initPayment(state.gameId, paymentData)

      if (paymentUrl) {
        window.location.href = paymentUrl
        return
      }

      this.bookingStatus = 'finished'
      this.stateManager.setState({ step: 'success' })
      this.uiUpdater.updateAll()
      this.updateCurrentStepSubmitState(false)
    } catch (error) {
      this.validator.showError(
        error instanceof Error && error.message
          ? error.message
          : 'Ошибка инициализации оплаты'
      )
      this.updateCurrentStepSubmitState(true)
    }
  }

  private isSlotPrepayRequired(state: BookingState): boolean {
    const value = state.slot?.prepay?.prepay_required

    return value === true || value === 'true'
  }

  private getOrderTotal(state: BookingState): number {
    const subtotal =
      state.bookingPrice !== null
        ? state.bookingPrice
        : this.stateManager.calculateBookingBaseTotal() +
          this.stateManager.calculateServicesTotal()
    // Если промокод уже применён сервером к брони (state.promoDiscount.amount
    // задан), скидка уже учтена в subtotal — вычитать её ещё раз не нужно.
    // До бронирования скидка считается от цены игры, без услуг.
    const promoDiscount =
      state.promoDiscount && state.promoDiscount.amount === undefined
        ? this.stateManager.calculatePromoDiscount(
            this.stateManager.calculateBookingBaseTotal()
          )
        : 0
    const certificateDiscount = Math.max(
      0,
      Number(state.certificateDiscount || 0)
    )

    return Math.max(0, subtotal - promoDiscount - certificateDiscount)
  }

  private buildPaymentDataForPay(state: BookingState): {
    booking_id: string
    payment_type: string
    partial: boolean
    pay_now_amount: number
    prepay_amount: number
    pay_later_amount: number
    prepay_only_checked?: boolean
    prepay_full_checked?: boolean
  } {
    const prepayOnlyCheckbox = this.modal.querySelector(
      '[data-booking-step="3"] input[name="prepay-only"]'
    ) as HTMLInputElement | null
    const prepayFullCheckbox = this.modal.querySelector(
      '[data-booking-step="3"] input[name="prepay-full"]'
    ) as HTMLInputElement | null

    const isCheckboxActive = (checkbox: HTMLInputElement | null): boolean =>
      Boolean(checkbox && !checkbox.disabled && !checkbox.closest('.is-hidden'))

    const prepayOnlyActive =
      state.paymentTiming === 'prepay' || isCheckboxActive(prepayOnlyCheckbox)
    const prepayFullActive =
      state.paymentTiming === 'full' || isCheckboxActive(prepayFullCheckbox)
    const prepayOnlyChecked =
      state.paymentTiming === 'prepay' ||
      (prepayOnlyActive && Boolean(prepayOnlyCheckbox?.checked))
    const prepayFullChecked =
      state.paymentTiming === 'full' ||
      (prepayFullActive && Boolean(prepayFullCheckbox?.checked))

    const orderTotal = this.getOrderTotal(state)
    const prepayAmount = Math.max(0, Number(state.prepayAmount || 0))
    const onlinePaymentTotal =
      state.prepayAmount === null
        ? orderTotal
        : Math.min(orderTotal, prepayAmount)
    const fullPaymentAmount = resolveFullPaymentAmount(state, orderTotal)

    let payNowAmount = 0
    let partial = false

    if (orderTotal === 0) {
      payNowAmount = 0
      partial = false
    } else if (state.paymentTiming === 'prepay') {
      payNowAmount = prepayAmount
      partial = true
    } else if (state.paymentTiming === 'full') {
      payNowAmount = fullPaymentAmount
      partial = payNowAmount < orderTotal
    } else if (
      state.prepayMode === 'optional' ||
      state.prepayMode === 'optional_partial'
    ) {
      if (prepayFullChecked) {
        payNowAmount = onlinePaymentTotal
      } else if (prepayOnlyChecked) {
        payNowAmount = prepayAmount
      }
      partial = prepayOnlyChecked
    } else if (state.prepayMode === 'optional_full') {
      payNowAmount = prepayFullChecked ? onlinePaymentTotal : 0
      partial = payNowAmount > 0 && payNowAmount < orderTotal
    } else if (
      state.prepayMode === 'required' ||
      state.prepayMode === 'partial_required'
    ) {
      payNowAmount = prepayFullChecked ? onlinePaymentTotal : prepayAmount
      partial = payNowAmount < orderTotal
    } else if (state.prepayMode === 'full_required') {
      payNowAmount = onlinePaymentTotal
      partial = payNowAmount < orderTotal
    }

    const payLaterAmount = Math.max(0, orderTotal - payNowAmount)

    return {
      booking_id: state.gameId || '',
      payment_type:
        state.paymentTiming === 'later' ? 'cash' : state.paymentType,
      partial,
      pay_now_amount: payNowAmount,
      prepay_amount: prepayAmount,
      pay_later_amount: payLaterAmount,
      ...(prepayOnlyActive ? { prepay_only_checked: prepayOnlyChecked } : {}),
      ...(prepayFullActive ? { prepay_full_checked: prepayFullChecked } : {})
    }
  }

  private getServicesPayload(): Array<{ service_id: number; number: number }> {
    return Object.entries(this.stateManager.getState().services)
      .map(([serviceId, count]) => ({
        service_id: Number(serviceId),
        number: Number(count)
      }))
      .filter(
        (service) => !Number.isNaN(service.service_id) && service.number > 0
      )
  }

  private servicesStateFromPayload(
    services?: Array<{ service_id: number; number: number }>
  ): BookingState['services'] {
    return (services || []).reduce<BookingState['services']>((result, item) => {
      const serviceId = Number(item.service_id)
      const count = Number(item.number)

      if (Number.isFinite(serviceId) && serviceId > 0 && count > 0) {
        result[serviceId] = count
      }

      return result
    }, {})
  }

  /**
   * Способы оплаты (динамически генерируется в UI)
   */
  private bindPaymentMethods(): void {
    // Динамически генерируется в updateUI
  }

  /**
   * Дополнительные услуги
   */
  private bindAdditionalServices(): void {
    const toggleButtons = this.modal.querySelectorAll(
      '[data-services-toggle]'
    ) as NodeListOf<HTMLElement>

    toggleButtons.forEach((toggleButton) => {
      if (toggleButton.dataset.servicesToggleBound === 'true') return

      toggleButton.addEventListener('click', (event) => {
        event.preventDefault()

        const stepContainer = toggleButton.closest(
          '[data-booking-step]'
        ) as HTMLElement | null
        const servicesList = stepContainer?.querySelector(
          '[data-services-list]'
        ) as HTMLElement | null

        const isOpen = toggleButton.classList.toggle('open')

        if (servicesList) {
          servicesList
            .querySelectorAll<HTMLElement>('[data-service-extra="true"]')
            .forEach((serviceItem) => {
              serviceItem.style.display = isOpen ? '' : 'none'
            })
        }

        const toggleText = toggleButton.querySelector('[data-toggle-text]')
        if (toggleText) {
          toggleText.textContent = isOpen ? 'Скрыть' : 'Показать еще'
        }
      })

      toggleButton.dataset.servicesToggleBound = 'true'
    })
  }

  /**
   * Сертификат
   */
  private bindCertificateCode(): void {
    const certificateWrapper = this.modal.querySelector(
      '[data-certificate]'
    ) as HTMLElement
    const certificateNumberInput = this.modal.querySelector(
      'input[name="certificate_number"]'
    ) as HTMLInputElement
    const certificateCodeInput = this.modal.querySelector(
      'input[name="certificate_code"]'
    ) as HTMLInputElement
    const certificateButton = this.modal.querySelector(
      '[data-certificate-apply]'
    ) as HTMLButtonElement
    const errorElement = this.modal.querySelector(
      '[data-certificate-error]'
    ) as HTMLElement

    if (
      !certificateWrapper ||
      !certificateNumberInput ||
      !certificateCodeInput ||
      !certificateButton ||
      !errorElement
    ) {
      console.warn('Элементы сертификата не найдены:', {
        certificateWrapper: !!certificateWrapper,
        certificateNumberInput: !!certificateNumberInput,
        certificateCodeInput: !!certificateCodeInput,
        certificateButton: !!certificateButton,
        errorElement: !!errorElement
      })
      return
    }

    // Настроить маску форматирования для кода сертификата
    setupCertificateCodeMask(certificateCodeInput)

    // Обработка ввода номера сертификата
    certificateNumberInput.addEventListener('input', () => {
      this.updateCertificateButtonState()
    })

    // Обработка ввода кода сертификата
    certificateCodeInput.addEventListener('input', () => {
      this.updateCertificateButtonState()
    })

    // Обработка клика кнопки применить
    certificateButton.addEventListener('click', (e) => {
      e.preventDefault()
      const number = certificateNumberInput.value.trim()
      const code = certificateCodeInput.value.trim()

      if (number && code) {
        this.applyCertificate(
          number,
          code,
          certificateWrapper,
          errorElement,
          certificateButton
        )
      }
    })

    // Обработка Enter в полях
    ;[certificateNumberInput, certificateCodeInput].forEach((input) => {
      input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' && !certificateButton.disabled) {
          certificateButton.click()
        }
      })
    })
  }

  /**
   * Обновить состояние кнопки применения сертификата
   */
  private updateCertificateButtonState(): void {
    const certificateNumberInput = this.modal.querySelector(
      'input[name="certificate_number"]'
    ) as HTMLInputElement
    const certificateCodeInput = this.modal.querySelector(
      'input[name="certificate_code"]'
    ) as HTMLInputElement
    const certificateButton = this.modal.querySelector(
      '[data-certificate-apply]'
    ) as HTMLButtonElement
    const certificateWrapper = this.modal.querySelector(
      '[data-certificate]'
    ) as HTMLElement
    const errorElement = this.modal.querySelector(
      '[data-certificate-error]'
    ) as HTMLElement

    if (
      !certificateNumberInput ||
      !certificateCodeInput ||
      !certificateButton ||
      !certificateWrapper ||
      !errorElement
    ) {
      return
    }

    const number = certificateNumberInput.value.trim()
    const code = certificateCodeInput.value.trim()

    // Включить кнопку если оба поля заполнены
    certificateButton.disabled = !number || !code

    // Любое изменение полей снимает применение: на сервер уйдёт только тот
    // сертификат, который клиент явно применил и подтвердил.
    certificateWrapper.classList.remove('error', 'accepted')
    errorElement.style.display = 'none'
    this.stateManager.setState({
      certificateNumber: '',
      certificateCode: '',
      certificateDiscount: null
    })
    this.uiUpdater.updatePrice()

    // Вернуть текст кнопки к "применить"
    const buttonText = certificateButton.querySelector(
      '[data-certificate-button-text]'
    ) as HTMLElement
    if (buttonText) {
      buttonText.textContent = 'Применить'
    }
  }

  /**
   * Проверить сертификат до бронирования (номер + код → номинал). Само
   * списание делает сервер вместе с отправкой брони партнёру, когда клиент
   * нажмёт «Забронировать» (Api::BookingController#complete_booking).
   */
  private async applyCertificate(
    number: string,
    code: string,
    certificateWrapper: HTMLElement,
    errorElement: HTMLElement,
    certificateButton: HTMLButtonElement
  ): Promise<void> {
    // Сброс предыдущих состояний
    certificateWrapper.classList.remove('error', 'accepted')
    errorElement.style.display = 'none'

    const originalButtonText = certificateButton?.querySelector(
      '[data-certificate-button-text]'
    )?.textContent
    certificateButton.disabled = true
    const buttonText = certificateButton.querySelector(
      '[data-certificate-button-text]'
    ) as HTMLElement
    if (buttonText) {
      buttonText.textContent = 'проверка...'
    }

    if (!this.isValidCertificateCode(code)) {
      certificateWrapper.classList.add('error')
      errorElement.textContent = 'Неверный формат кода сертификата'
      errorElement.style.color = 'var(--red)'
      errorElement.style.display = 'block'
      certificateButton.disabled = false
      if (buttonText) {
        buttonText.textContent = originalButtonText || 'Применить'
      }
      return
    }

    try {
      const state = this.stateManager.getState()

      if (!state.quest || !state.slot) {
        throw new Error('Не выбран квест или время')
      }

      const unit =
        document
          .querySelector('meta[name="app:currency"]')
          ?.getAttribute('content') || '₽'
      const questCurrency = unit === 'Br' || unit === 'BYN' ? 'BYN' : 'RUB'
      // Сертификат в другой валюте сервер пересчитывает по курсу ЦБ РФ.
      const result = await this.api.checkCertificate(
        number,
        code,
        questCurrency
      )

      const giftValue = Number(result.gift_value || 0)
      if (!giftValue || giftValue <= 0) {
        throw new Error('Сертификат не содержит доступной суммы')
      }

      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        'При бронировании сертификат будет погашен полностью. Для переноса неиспользованного остатка напишите на helpme@claustrophobia.com. Оставшуюся сумму брони можно доплатить на месте. Применить сертификат?'
      )
      if (!confirmed) return

      certificateWrapper.classList.add('accepted')
      this.stateManager.setState({
        certificateNumber: number,
        certificateCode: code,
        certificateDiscount: giftValue,
        certificateCurrency: result.currency || 'RUB'
      })
      this.uiUpdater.updatePrice()

      if (buttonText) {
        buttonText.textContent = 'принят'
      }

      const giftUnit = result.currency === 'BYN' ? 'BYN' : '₽'
      const conversion =
        result.nominal_value && result.exchange_rate_date
          ? ` (номинал ${result.nominal_value} ${result.nominal_currency === 'BYN' ? 'BYN' : '₽'} по курсу ЦБ РФ на ${result.exchange_rate_date.split('-').reverse().join('.')})`
          : ''
      renderWithCurrency(
        errorElement,
        `Сертификат на ${giftValue} ${giftUnit}${conversion} будет списан при бронировании`
      )
      errorElement.style.color = '#4CAF50'
      errorElement.style.display = 'block'
    } catch (error) {
      certificateWrapper.classList.add('error')
      errorElement.textContent =
        error instanceof Error
          ? error.message
          : 'Сертификат неверный или не может быть применен'
      errorElement.style.color = 'var(--red)'
      errorElement.style.display = 'block'
    } finally {
      // Поля остаются редактируемыми: если сервер отклонит сертификат при
      // бронировании, клиент сможет исправить номер или убрать его.
      const hasValues = Boolean(number && code)
      certificateButton.disabled =
        !hasValues || certificateWrapper.classList.contains('accepted')
      if (buttonText && !certificateWrapper.classList.contains('accepted')) {
        buttonText.textContent = originalButtonText || 'Применить'
      }
    }
  }

  private isValidCertificateCode(code: string): boolean {
    // Код с бланка набирают с пробелами и подчас кириллицей —
    // «KCEH CMT7 7PA7 H3H6»; сервер сам приводит написание.
    const certificateRegex = /^[A-Za-zА-Яа-яЁё0-9\s]+$/
    return certificateRegex.test(code.trim())
  }

  /**
   * Промокод
   */
  autoApplyPromoCode(): void {
    const input = this.modal.querySelector<HTMLInputElement>(
      '[name="promo_code"]'
    )
    if (!input?.value.trim()) return

    this.stateManager.setState({
      promoCode: input.value.trim(),
      promoDiscount: null
    })
    const toggle = this.modal.querySelector<HTMLButtonElement>(
      '#booking-promo-button'
    )
    if (toggle?.getAttribute('aria-expanded') === 'false') toggle.click()
    const button =
      this.modal.querySelector<HTMLButtonElement>('[data-promo-apply]')
    if (button) {
      button.disabled = false
      button.click()
    }
  }

  private bindPromoCode(): void {
    const promoWrapper = this.modal.querySelector(
      '[data-promo-code]'
    ) as HTMLElement
    // Ищем input по имени, так как data-promo-input добавляется к input внутри компонента
    const promoInput = this.modal.querySelector(
      'input[name="promo_code"]'
    ) as HTMLInputElement
    const promoButton = this.modal.querySelector(
      '[data-promo-apply]'
    ) as HTMLButtonElement
    const errorElement = this.modal.querySelector(
      '[data-promo-error]'
    ) as HTMLElement

    if (!promoWrapper || !promoInput || !promoButton || !errorElement) {
      console.warn('Элементы промокода не найдены:', {
        promoWrapper: !!promoWrapper,
        promoInput: !!promoInput,
        promoButton: !!promoButton,
        errorElement: !!errorElement
      })
      return
    }

    // Обработка ввода промокода
    this.modal
      .querySelectorAll<HTMLInputElement>('[name="phone"], [name="email"]')
      .forEach((input) => {
        input.addEventListener('change', () => this.autoApplyPromoCode())
      })

    promoInput.addEventListener('input', (e) => {
      this.promoRequest += 1
      const value = (e.target as HTMLInputElement).value.trim()

      // Включить/выключить кнопку
      promoButton.disabled = value.length === 0

      // Сбросить состояние ошибки и принятия
      promoWrapper.classList.remove('error', 'accepted')
      errorElement.style.display = 'none'

      // Вернуть текст кнопки к "применить"
      const buttonText = promoButton.querySelector(
        '[data-promo-button-text]'
      ) as HTMLElement
      if (buttonText) {
        buttonText.textContent = 'Применить'
      }

      // Сбросить скидку и обновить цену
      this.stateManager.setState({
        promoCode: value,
        promoDiscount: null
      })
      this.uiUpdater.updatePrice()
    })

    // Обработка клика по кнопке применить
    promoButton.addEventListener('click', () => {
      const promoCode = promoInput.value.trim()
      if (!promoCode) return

      this.applyPromoCode(promoCode, promoWrapper, errorElement)
    })

    // Обработка Enter в поле промокода
    promoInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        const promoCode = promoInput.value.trim()
        if (promoCode && !promoButton.disabled) {
          this.applyPromoCode(promoCode, promoWrapper, errorElement)
        }
      }
    })
  }

  private promoRequest = 0

  private async applyPromoCode(
    promoCode: string,
    promoWrapper: HTMLElement,
    errorElement: HTMLElement
  ): Promise<void> {
    const requestId = ++this.promoRequest
    this.stateManager.setState({ promoCode, promoDiscount: null })
    this.uiUpdater.updatePrice()
    // Сброс предыдущих состояний
    promoWrapper.classList.remove('error', 'accepted')
    errorElement.style.display = 'none'

    // Блокируем кнопку на время запроса
    const promoButton = promoWrapper.querySelector(
      '[data-promo-apply]'
    ) as HTMLButtonElement
    if (promoButton) {
      promoButton.disabled = true
      const buttonText = promoButton.querySelector(
        '[data-promo-button-text]'
      ) as HTMLElement
      if (buttonText) {
        buttonText.textContent = 'проверка...'
      }
    }

    try {
      const state = this.stateManager.getState()

      if (!state.quest || !state.slot) {
        throw new Error('Не выбран квест или время')
      }

      const email = this.getStepOneInputValue('email')
      const phone = this.getPhoneValue()

      if (!email || !phone) {
        throw new Error('Заполните email и телефон для применения промокода')
      }

      const response = await this.api.validatePromoCode({
        code: promoCode,
        questId: state.quest.quest_id,
        date: state.slot.date,
        time: state.slot.time,
        email,
        phone,
        ...(state.gameId ? { id: state.gameId } : {})
      })

      if (requestId !== this.promoRequest) return

      if (!response.data) {
        throw new Error('Промокод недействителен')
      }

      const discountInfo = {
        code: promoCode,
        type:
          response.data.discount_kind === 'percent'
            ? ('percent' as const)
            : ('money' as const),
        value: response.data.discount_value ?? 0,
        ...(response.data.discount_amount !== undefined
          ? { amount: response.data.discount_amount }
          : {})
      }

      // Промокод валидный - отображаем успех и сохраняем скидку. Если промокод
      // применён к уже созданной брони (шаг 2/3), сервер пересчитал её цену —
      // подтягиваем свежие суммы, иначе предоплата продолжит считаться от старой,
      // недисконтированной цены (как это делает syncAdditionalServices).
      promoWrapper.classList.add('accepted')
      this.stateManager.setState({
        promoCode,
        promoDiscount: discountInfo,
        ...(response.data.booking_price !== undefined
          ? { bookingPrice: response.data.booking_price }
          : {}),
        ...(response.data.services_price !== undefined
          ? { servicesPrice: response.data.services_price }
          : {}),
        ...(response.data.online_payment_price !== undefined
          ? { prepayAmount: response.data.online_payment_price }
          : {})
      })

      // Обновляем цену с учетом скидки
      this.uiUpdater.updatePrice()

      // Обновляем текст кнопки
      if (promoButton) {
        const buttonText = promoButton.querySelector(
          '[data-promo-button-text]'
        ) as HTMLElement
        if (buttonText) {
          buttonText.textContent = 'принят'
        }
      }

      // Показываем сообщение об успешном применении промокода
      errorElement.textContent = 'Промокод применён'
      errorElement.style.color = '#4CAF50'
      errorElement.style.display = 'block'
    } catch (error) {
      if (requestId !== this.promoRequest) return
      // Промокод недействительный, сеть или другая ошибка
      promoWrapper.classList.add('error')
      errorElement.textContent =
        error instanceof Error ? error.message : 'Промокод недействителен'
      errorElement.style.color = 'var(--red)'
      errorElement.style.display = 'block'
    } finally {
      // Только актуальный запрос может восстановить состояние кнопки.
      if (requestId === this.promoRequest && promoButton) {
        promoButton.disabled = false
        const buttonText = promoButton.querySelector(
          '[data-promo-button-text]'
        ) as HTMLElement
        if (buttonText && !promoWrapper.classList.contains('accepted')) {
          buttonText.textContent = 'Применить'
        }
      }
    }
  }

  /**
   * Дата посещения билетной системы (Quest#ticket_system) — она и есть дата
   * синтетического слота: её же отправляет submitBooking и показывает шапка.
   */
  private bindTicketVisitDate(): void {
    const input = this.modal.querySelector<HTMLInputElement>(
      '[data-ticket-visit-date-input]'
    )

    input?.addEventListener('change', () => {
      const state = this.stateManager.getState()
      if (!state.slot) return

      this.stateManager.setState({ slot: { ...state.slot, date: input.value } })
      this.uiUpdater.updateQuestInfo(this.modal, this.stateManager.getState())
    })
  }

  /**
   * Чекбокс согласия с офертой
   */
  private bindAgreeCheckbox(): void {
    const agreeCheckbox = this.modal.querySelector(
      'input[name="acceptance"]'
    ) as HTMLInputElement

    agreeCheckbox?.addEventListener('change', () => {
      this.updateCurrentStepSubmitState(true)
      this.uiUpdater.updateStepTwoButton()
    })
  }

  /**
   * Получить значение isUserAccepted
   * @returns {boolean} значение isUserAccepted
   */
  getIsUserAccepted(): boolean {
    return this.isUserAccepted
  }

  /**
   * Сбросить isUserAccepted
   */
  resetUserAccepted(): void {
    this.isUserAccepted = false
  }
}
